// Add-entry dialog shared by both the blacklist and allowlist sections. The
// available pattern types differ per list (the allowlist has no "wildcard"),
// so the caller passes the type options. On success it dispatches addEmailEntry
// and closes once the write resolves.
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert,
  Box, CircularProgress, MenuItem,
} from '@mui/material';
import { addEmailEntry, clearSaveError } from '../../../store/admin-blacklist-slice.js';

const TYPE_HELP = {
  domain: 'Matches the domain and its subdomains (e.g. example.com).',
  exact: 'Matches one full email address (e.g. user@example.com).',
  wildcard: 'Matches a pattern; * stands for any characters (e.g. *@*.example.*).',
};

const AddEntryDialog = ({
  open, list, title, typeOptions, onClose,
}) => {
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((s) => s.adminBlacklist);
  const [pattern, setPattern] = useState('');
  const [patternType, setPatternType] = useState(typeOptions[0]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setPattern('');
      setPatternType(typeOptions[0]);
      setReason('');
      dispatch(clearSaveError());
    }
  }, [open, typeOptions, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await dispatch(addEmailEntry({
      list,
      values: {
        pattern: pattern.trim().toLowerCase(),
        pattern_type: patternType,
        reason: reason.trim() || null,
      },
    }));
    if (addEmailEntry.fulfilled.match(result)) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 2,
        }}
        >
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField
            select
            label="Type"
            value={patternType}
            onChange={(e) => setPatternType(e.target.value)}
            helperText={TYPE_HELP[patternType]}
          >
            {typeOptions.map((type) => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Pattern"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            required
            autoFocus
            slotProps={{ htmlInput: { maxLength: 255 } }}
            placeholder={patternType === 'exact' ? 'user@example.com' : 'example.com'}
          />
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            helperText="A short note for other admins on why this was added."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Add'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AddEntryDialog;
